import { useRef, useState } from 'react';
import { Box, TextField, IconButton, CircularProgress, Typography } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ClearIcon from '@mui/icons-material/Clear';
import PropTypes from 'prop-types';
import { handleWarningSwal } from '../swals';

const MAX_FILE_SIZE_MB = 10;
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

const ChatInput = ({ input, onInputChange, onSend, loading }) => {
  const fileInputRef = useRef();
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState('');

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    if (!ALLOWED_IMAGE_TYPES.includes(selectedFile.type)) {
      handleWarningSwal('Solo se permiten imágenes JPG, PNG, WEBP o GIF.');
      return;
    }

    const fileSizeMB = selectedFile.size / (1024 * 1024);
    if (fileSizeMB > MAX_FILE_SIZE_MB) {
      handleWarningSwal('El archivo excede los 10MB permitidos.');
      return;
    }

    setFile(selectedFile);
    setFileName(selectedFile.name);
    e.target.value = '';
  };

  const handleSend = async () => {
    if (loading) return;

    if (file) {
      await onSend({ type: 'image', message: file });
      handleRemoveFile();
    } else if (input.trim()) {
      await onSend({ type: 'text', message: input.trim() });
    }
  };

  const handleRemoveFile = () => {
    setFile(null);
    setFileName('');
  };
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        maxHeight: 90,
        bgcolor: '#DFDFDF',
        mt: 1,
        p: 1.6,
      }}
    >
      {fileName ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            bgcolor: '#f0f0f0',
            borderRadius: 1.5,
            boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
            fontWeight: 500,
            width: '100%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          <Typography variant="caption" noWrap sx={{ flexGrow: 1, ml: 1 }}>
            Archivo: {fileName}
          </Typography>
          <IconButton onClick={handleRemoveFile} sx={{ color: '#6b6b6b' }}>
            <ClearIcon />
          </IconButton>
        </Box>
      ) : (
        <TextField
          fullWidth
          placeholder="Escriba su mensaje..."
          value={input}
          onChange={(e) => onInputChange(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={loading}
          multiline
          minRows={1}
          maxRows={3}
          InputProps={{
            sx: {
              fontSize: 14,
              paddingY: '12px',
              paddingX: '8px',
              bgcolor: '#ffffff',
              borderRadius: 2,
              overflowY: 'auto',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#d6d3ce',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#b8b4ae',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#8a8882',
              },
            },
          }}
        />
      )}
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        accept="image/*"
        onChange={handleFileChange}
      />
      <IconButton
        onClick={() => fileInputRef.current?.click()}
        disabled={loading}
        sx={{ ml: 1, color: '#6b6b6b' }}
      >
        <AttachFileIcon />
      </IconButton>
      <IconButton
        onClick={handleSend}
        disabled={loading || (!input.trim() && !file)}
        sx={{ ml: 1, color: '#6b6b6b' }}
      >
        {loading ? <CircularProgress size={22} /> : <SendIcon />}
      </IconButton>
    </Box>
  );
};

ChatInput.propTypes = {
  input: PropTypes.string.isRequired,
  onInputChange: PropTypes.func.isRequired,
  onSend: PropTypes.func.isRequired,
  loading: PropTypes.bool,
};

export default ChatInput;
